import { useCallback } from 'react'
import accountLinkApi from 'src/Libs/installation-rights-management/api/account-link.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { ApiPermissionType } from 'src/Libs/api/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { usePortalMutation } from 'src/Libs/api'

interface UseUserAccessAndInvitationsHook {
  /**
   * Change the write access of a single AccountLink
   * @param item
   * @param canWrite
   */
  toggleWriteAccess: (item: AccountLinkModel, canWrite: boolean, hasApiAccess: boolean) => Promise<void>

  /**
   * Removes the provided AccountLink from accessing the related Installation.
   *
   * @param installationId
   * @param accountLinkId
   */
  removeAccountFromAccess: (installationId: number, accountLinkId: number, reason?: string) => Promise<void>
}

type UseUserAccessAndInvitationsParams = {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

type UseUserAccessAndInvitations = (params?: UseUserAccessAndInvitationsParams) => UseUserAccessAndInvitationsHook

type ToggleWriteAccessVariables = {
  item: AccountLinkModel
  canWrite: boolean
  hasHagerEnergyApiAccess: boolean
}

type RemoveAccountFromAccessVariables = {
  installationId: number
  accountLinkId: number
  reason?: string
}

/**
 * This hook provides utils to support the ui components in changing an existing Account assignment to an Installation
 *
 */
const useUserAccessAndInvitations: UseUserAccessAndInvitations = params => {
  const { onSuccess, onError } = params ?? {}
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const toggleWriteAccessMutation = usePortalMutation<void, Error, ToggleWriteAccessVariables>({
    mutationFn: ({ item, canWrite, hasHagerEnergyApiAccess }) => {
      const permission = canWrite ? ApiPermissionType.rw : ApiPermissionType.ro

      return accountLinkApi.update(
        item.installationId,
        item.id,
        {
          inviteId: item.inviteId ? item.inviteId : undefined,
          permissionType: permission,
          storagePermissionType: permission,
          endUserPermissionType: permission,
          eMobilityPermissionType: permission,
          hasHagerEnergyApiAccess,
        },
        authFetch
      )
    },
    onSuccess: () => {
      enqueueSuccessStack(t('installation.access-and-invitations.toggle-write-access.success'))
    },
    onError: () => {
      enqueueErrorStack(t('installation.access-and-invitations.toggle-write-access.failed'))
    },
  })

  const removeAccountFromAccessMutation = usePortalMutation<void, Error, RemoveAccountFromAccessVariables>({
    mutationFn: ({ installationId, accountLinkId, reason }) =>
      accountLinkApi.removeOne({
        installationId,
        accountLinkId,
        authFetch,
        reason,
      }),
    onSuccess: () => {
      onSuccess?.()
    },
    onError: error => {
      onError?.(error)
    },
  })

  const toggleWriteAccess = useCallback(
    async (item: AccountLinkModel, canWrite = false, hasHagerEnergyApiAccess = false): Promise<void> => {
      await toggleWriteAccessMutation.mutateAsync({
        item,
        canWrite,
        hasHagerEnergyApiAccess,
      })
    },
    [toggleWriteAccessMutation]
  )

  const removeAccountFromAccess = useCallback(
    async (installationId: number, accountLinkId: number, reason?: string): Promise<void> => {
      await removeAccountFromAccessMutation.mutateAsync({
        installationId,
        accountLinkId,
        reason,
      })
    },
    [removeAccountFromAccessMutation]
  )

  return { toggleWriteAccess, removeAccountFromAccess }
}

export default useUserAccessAndInvitations
